import { useState, useEffect, useCallback } from 'react';
import { pdiStateDB, photosDB, carProfileDB, resetAllData } from '../utils/db';
import { 
  CHECKLIST_SECTIONS, 
  calculateSectionProgress, 
  calculateOverallProgress,
  calculatePDIScore,
} from '../data/checklistData';

/**
 * Custom hook to manage PDI state with IndexedDB persistence
 */
export const usePDI = () => {
  const [itemStatuses, setItemStatuses] = useState({});
  const [notes, setNotes] = useState({});
  const [resolvedItems, setResolvedItems] = useState({});
  const [photos, setPhotos] = useState({});
  const [carProfile, setCarProfile] = useState(null);
  const [hasSeenGuidance, setHasSeenGuidance] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // Load initial data from IndexedDB
  useEffect(() => {
    const loadData = async () => {
      try {
        const [
          savedStatuses,
          savedNotes,
          savedResolved,
          savedProfile,
          seenGuidance,
        ] = await Promise.all([
          pdiStateDB.getItemStatuses(),
          pdiStateDB.getNotes(),
          pdiStateDB.getResolvedItems(),
          carProfileDB.getProfile(),
          pdiStateDB.getHasSeenGuidance(),
        ]);

        setItemStatuses(savedStatuses);
        setNotes(savedNotes);
        setResolvedItems(savedResolved);
        setCarProfile(savedProfile);
        setHasSeenGuidance(seenGuidance);

        // Load photos grouped by item
        const allPhotos = await photosDB.getAllPhotos();
        const photosByItem = {};
        allPhotos.forEach(photo => {
          if (!photosByItem[photo.itemId]) {
            photosByItem[photo.itemId] = [];
          }
          photosByItem[photo.itemId].push(photo);
        });
        setPhotos(photosByItem);
      } catch (error) {
        console.error('Error loading PDI data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Show toast notification
  const showToast = useCallback((message) => {
    setToast(message);
    setTimeout(() => setToast(null), 3000);
  }, []);

  // Update item status
  const updateItemStatus = useCallback(async (itemId, status) => {
    const newStatuses = { ...itemStatuses, [itemId]: status };
    setItemStatuses(newStatuses);
    await pdiStateDB.saveItemStatuses(newStatuses);
  }, [itemStatuses]);

  // Mark all items in a section as passed
  const markSectionAsPass = useCallback(async (sectionId, items) => {
    const newStatuses = { ...itemStatuses };
    items.forEach(item => {
      newStatuses[item.id] = 'pass';
    });
    setItemStatuses(newStatuses);
    await pdiStateDB.saveItemStatuses(newStatuses);
    showToast('All items marked as passed');
  }, [itemStatuses, showToast]);

  // Update note for an item
  const updateNote = useCallback(async (itemId, note) => {
    const newNotes = { ...notes, [itemId]: note };
    setNotes(newNotes);
    await pdiStateDB.saveNotes(newNotes);
  }, [notes]);

  // Mark item as resolved by dealer
  const markAsResolved = useCallback(async (itemId, resolved) => {
    const newResolved = { ...resolvedItems, [itemId]: resolved };
    setResolvedItems(newResolved);
    await pdiStateDB.saveResolvedItems(newResolved);
    if (resolved) {
      showToast('Marked as resolved by dealer');
    }
  }, [resolvedItems, showToast]);

  // Add photo to an item
  const addPhoto = useCallback(async (itemId, photoData) => {
    const photoId = await photosDB.addPhoto(itemId, photoData);
    const newPhoto = { id: photoId, itemId, data: photoData, timestamp: Date.now() };
    setPhotos(prev => ({
      ...prev,
      [itemId]: [...(prev[itemId] || []), newPhoto],
    }));
    showToast('Photo added');
    return photoId;
  }, [showToast]);

  // Delete photo
  const deletePhoto = useCallback(async (photoId, itemId) => {
    await photosDB.deletePhoto(photoId);
    setPhotos(prev => ({
      ...prev,
      [itemId]: (prev[itemId] || []).filter(p => p.id !== photoId),
    }));
    showToast('Photo deleted');
  }, [showToast]);

  // Replace a photo's image data (used after annotating/marking up)
  const updatePhoto = useCallback(async (photoId, itemId, newData) => {
    await photosDB.updatePhoto(photoId, newData);
    setPhotos(prev => ({
      ...prev,
      [itemId]: (prev[itemId] || []).map(p =>
        p.id === photoId ? { ...p, data: newData } : p
      ),
    }));
    showToast('Photo marked up');
  }, [showToast]);

  // Update car profile
  const updateCarProfile = useCallback(async (profile) => {
    setCarProfile(profile);
    await carProfileDB.saveProfile(profile);
    showToast('Car profile saved');
  }, [showToast]);

  // Mark guidance as seen
  const markGuidanceSeen = useCallback(async () => {
    setHasSeenGuidance(true);
    await pdiStateDB.setHasSeenGuidance(true);
  }, []);

  // Reset PDI (keep guidance seen)
  const resetPDI = useCallback(async () => {
    setItemStatuses({});
    setNotes({});
    setResolvedItems({});
    setPhotos({});

    await pdiStateDB.clearAll();
    await photosDB.clearAll();
    await pdiStateDB.setHasSeenGuidance(true);

    showToast('PDI checklist reset');
  }, [showToast]);

  // Clear all data including guidance
  const clearAllData = useCallback(async () => {
    setItemStatuses({});
    setNotes({});
    setResolvedItems({});
    setPhotos({});
    setCarProfile(null);
    setHasSeenGuidance(false);

    await resetAllData();

    showToast('All data cleared');
  }, [showToast]);

  // Get selected features from car profile
  const selectedFeatures = carProfile?.features || [];

  // Calculate progress for each section
  const sectionProgress = CHECKLIST_SECTIONS.reduce((acc, section) => {
    acc[section.id] = calculateSectionProgress(section.id, itemStatuses);
    return acc;
  }, {});

  // Calculate overall progress (including feature-based items)
  const overallProgress = calculateOverallProgress(itemStatuses, selectedFeatures);

  // Calculate PDI score (including feature-based items)
  const pdiScore = calculatePDIScore(itemStatuses, selectedFeatures);

  return {
    // State
    itemStatuses,
    notes,
    resolvedItems,
    photos,
    carProfile,
    hasSeenGuidance,
    isLoading,
    toast,
    
    // Computed
    sectionProgress,
    overallProgress,
    pdiScore,
    
    // Actions
    updateItemStatus,
    markSectionAsPass,
    updateNote,
    markAsResolved,
    addPhoto,
    deletePhoto,
    updatePhoto,
    updateCarProfile,
    markGuidanceSeen,
    resetPDI,
    clearAllData,
    showToast,
  };
};

export default usePDI;
